import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { CourseQuiz } from '../components/CourseQuiz';
import { ExerciseSection } from '../components/ExerciseSection';
import { useCourseContent } from '../hooks/useContentData';
import { CULTURAL_ANSWERS, CULTURAL_SECTIONS } from '../data/quizBank';
import EXERCISES_CULTURAL from '../data/exerciseBankCultural';

const API = process.env.REACT_APP_BACKEND_URL;

function PracticeButton({ text }) {
  return (
    <span className="inline-flex items-center gap-1">
      <AudioButton text={text} />
      <MicrophoneButton expectedText={text} />
    </span>
  );
}

const MODULE_DEFS = [
  { id: 'c1', label: 'Begrüßung', icon: '🤝', dataKey: 'greetingsData' },
  { id: 'c2', label: 'Essen', icon: '🍽️', dataKey: 'foodData' },
  { id: 'c3', label: 'Feiertage', icon: '🎄', dataKey: 'holidaysData' },
  { id: 'c4', label: 'Arbeitskultur', icon: '💼', dataKey: 'workCultureData' },
  { id: 'c5', label: 'Gesundheit', icon: '🏥', dataKey: 'healthcareData' },
  { id: 'c6', label: 'Wohnen', icon: '🏠', dataKey: 'livingData' },
];

export default function CulturalLearning() {
  const [activeTab, setActiveTab] = useState('c1');
  const { lessons, loading } = useCourseContent('cultural');

  const MODULES = MODULE_DEFS.map(m => ({ ...m, data: lessons[m.dataKey] }));

  const renderSection = (section, si) => {
    if (section.type === 'vocab_table') {
      return (
        <div key={si} className="card">
          <div className="card-header">
            <span className="card-icon">{section.icon}</span>
            <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
          </div>
          <div className="card-body">
            <table className="vocab-table">
              <thead>
                <tr><th>Deutsch</th><th>English</th><th className="ar">عربي</th>{section.hasAudio && <th>Üben</th>}</tr>
              </thead>
              <tbody>
                {section.items.map((item, i) => (
                  <tr key={`${item.de?.slice(0, 20) || ''}-${i}`}>
                    <td className="de">{item.de}</td>
                    <td className="en">{item.en}</td>
                    <td className="ar">{item.ar}</td>
                    {section.hasAudio && <td><PracticeButton text={item.de} /></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (section.type === 'phrase_grid') {
      return (
        <div key={si} className="card">
          <div className="card-header">
            <span className="card-icon">{section.icon}</span>
            <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
          </div>
          <div className="card-body">
            <div className="phrase-grid">
              {section.items.map((item, i) => (
                <div key={`phrase-${item.de?.slice(0, 20) || i}`} className="phrase-card">
                  <div className="phrase-ctx">{item.ctx}</div>
                  <div className="phrase-de flex items-center justify-between">
                    <span>{item.de}</span>
                    <PracticeButton text={item.de} />
                  </div>
                  <div className="phrase-en">{item.en}</div>
                  <div className="phrase-ar">{item.ar}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (section.type === 'dialog') {
      return (
        <div key={si} className="dialog-box">
          <div className="dialog-title">{section.title}</div>
          <div className="dialog-content">
            {section.lines.map((line, j) => (
              <div key={`dialog-${line.speaker}-${line.de?.slice(0, 15) || j}`} className="dialog-line">
                <div className={`dialog-speaker ${line.speaker.toLowerCase()}`}>{line.speaker}:</div>
                <div className={`dialog-bubble ${line.speaker === 'Patient' || line.speaker === 'B' ? 'patient' : ''}`}>
                  <div className="de" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{line.de}</span>
                    <PracticeButton text={line.de} />
                  </div>
                  <div className="en">{line.en}</div>
                  <div className="ar">{line.ar}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (section.type === 'tip') {
      return (
        <div key={si} className="tip-box" style={{ marginTop: '16px' }}>
          <strong>Tipp · Tip · <span className="ar">نصيحة</span></strong><br />
          {section.text.de} · {section.text.en} · <span className="ar">{section.text.ar}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="pflege-page" data-testid="cultural-learning-page">
      <style>{`
        .pflege-page{font-family:'Segoe UI',system-ui,sans-serif;background:#0f172a;color:#e2e8f0;min-height:100vh}
        .pflege-page .ar{font-family:'Amiri','Noto Naskh Arabic',serif;direction:rtl}
        .top-bar{background:linear-gradient(135deg,#065f46 0%,#047857 100%);padding:16px 20px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50}
        .top-bar h1{font-size:18px;font-weight:700;color:white}
        .top-bar a{color:rgba(255,255,255,0.7);text-decoration:none;display:flex;align-items:center;gap:4px;font-size:14px}
        .top-bar a:hover{color:white}
        .tab-nav{display:flex;overflow-x:auto;gap:6px;padding:12px 16px;background:#1e293b;border-bottom:1px solid #334155;-webkit-overflow-scrolling:touch}
        .tab-nav::-webkit-scrollbar{display:none}
        .tab-btn{padding:8px 16px;border-radius:20px;border:1px solid #334155;background:transparent;color:#94a3b8;cursor:pointer;white-space:nowrap;font-size:13px;display:flex;align-items:center;gap:6px;transition:all .2s}
        .tab-btn:hover{border-color:#14b8a6;color:#14b8a6}
        .tab-btn.active{background:#14b8a6;color:white;border-color:#14b8a6}
        .content{max-width:960px;margin:0 auto;padding:20px 16px}
        .section{display:none}.section.active{display:block}
        .card{background:#1e293b;border-radius:12px;margin-bottom:16px;border:1px solid #334155;overflow:hidden}
        .card-header{padding:14px 18px;border-bottom:1px solid #334155;display:flex;align-items:center;gap:10px}
        .card-header h3{font-size:15px;font-weight:600;color:#e2e8f0;margin:0}
        .card-icon{font-size:22px}
        .card-body{padding:16px 18px}
        .vocab-table{width:100%;border-collapse:collapse}
        .vocab-table th{text-align:left;padding:8px;font-size:12px;color:#64748b;border-bottom:1px solid #334155;text-transform:uppercase}
        .vocab-table td{padding:8px;border-bottom:1px solid #1e293b;font-size:14px}
        .vocab-table td.de{color:#5eead4;font-weight:600}
        .vocab-table td.en{color:#e2e8f0}
        .vocab-table td.ar{color:#fbbf24}
        .phrase-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px}
        .phrase-card{background:#0f172a;border-radius:10px;padding:14px;border:1px solid #334155}
        .phrase-ctx{font-size:11px;color:#14b8a6;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
        .phrase-de{color:#5eead4;font-weight:600;font-size:15px;margin-bottom:4px}
        .phrase-en{color:#e2e8f0;font-size:13px}
        .phrase-ar{color:#fbbf24;font-size:14px;margin-top:4px}
        .dialog-box{background:#1e293b;border-radius:12px;margin-bottom:16px;border:1px solid #334155;overflow:hidden}
        .dialog-title{padding:14px 18px;border-bottom:1px solid #334155;font-weight:700;font-size:15px;color:#5eead4}
        .dialog-content{padding:16px 18px}
        .dialog-line{margin-bottom:14px}
        .dialog-speaker{font-size:12px;font-weight:700;color:#14b8a6;margin-bottom:4px;text-transform:uppercase}
        .dialog-bubble{background:#0f172a;border-radius:10px;padding:12px;border-left:3px solid #14b8a6}
        .dialog-bubble.patient{border-left-color:#818cf8}
        .dialog-bubble .de{color:#5eead4;font-weight:600;font-size:15px}
        .dialog-bubble .en{color:#e2e8f0;font-size:13px;margin-top:4px}
        .dialog-bubble .ar{color:#fbbf24;font-size:14px;margin-top:4px}
        .tip-box{background:rgba(20,184,166,.1);border:1px solid rgba(20,184,166,.3);border-radius:10px;padding:14px;font-size:13px;color:#e2e8f0;line-height:1.6}
        @media(max-width:600px){.phrase-grid{grid-template-columns:1fr}.vocab-table td,.vocab-table th{padding:6px;font-size:12px}}
      `}</style>

      <div className="top-bar">
        <Link to="/dashboard">
          <ChevronLeft className="h-4 w-4" /> Back
        </Link>
        <h1>EGNS Cultural Learning</h1>
        <div style={{ width: 60 }} />
      </div>

      <div className="tab-nav" data-testid="cultural-tab-nav">
        {MODULES.map(m => (
          <button
            key={m.id}
            className={`tab-btn ${activeTab === m.id ? 'active' : ''}`}
            onClick={() => setActiveTab(m.id)}
            data-testid={`tab-${m.id}`}
          >
            {m.icon} {m.label}
          </button>
        ))}
        <button
          className={`tab-btn ${activeTab === 'exercises' ? 'active' : ''}`}
          style={activeTab === 'exercises' ? {background:'#14b8a6',color:'white',borderColor:'#14b8a6'} : {borderColor:'rgba(20,184,166,0.4)',color:'#14b8a6'}}
          onClick={() => setActiveTab('exercises')}
          data-testid="tab-exercises"
        >
          🏋️ Übungen
        </button>
        <button
          className={`tab-btn ${activeTab === 'quiz' ? 'active' : ''}`}
          onClick={() => setActiveTab('quiz')}
          data-testid="tab-quiz"
        >
          ✅ Quiz
        </button>
      </div>

      <div className="content">
        {activeTab === 'exercises' && (
          <div className="section active" style={{ display: 'block' }} data-testid="cultural-exercises">
            <CulturalExercisePicker />
          </div>
        )}

        {activeTab === 'quiz' && (
          <div className="section active" style={{ display: 'block' }}>
            <style>{`
              .quiz-question{background:#1e293b;border-radius:12px;padding:16px;margin-bottom:12px;border:1px solid #334155}
              .quiz-question p{margin-bottom:10px;font-size:14px;line-height:1.5}
              .quiz-options{display:flex;flex-direction:column;gap:8px}
              .quiz-option{padding:10px 14px;border-radius:8px;border:1px solid #334155;cursor:pointer;font-size:13px;transition:all .2s}
              .quiz-option:hover{border-color:#14b8a6;background:rgba(20,184,166,.05)}
              .quiz-option.selected{border-color:#3b82f6;background:rgba(59,130,246,.1)}
              .quiz-option.correct{border-color:#10b981;background:rgba(16,185,129,.15);color:#6ee7b7}
              .quiz-option.wrong{border-color:#ef4444;background:rgba(239,68,68,.1);color:#fca5a5}
              .quiz-results{text-align:center;padding:30px;background:#1e293b;border-radius:16px;border:1px solid #334155}
              .quiz-score{font-size:48px;font-weight:800;color:#14b8a6}
              .quiz-score-text{color:#94a3b8;margin-bottom:12px}
              .quiz-message{font-size:15px;color:#e2e8f0;margin-bottom:8px}
              .quiz-label{font-size:14px;font-weight:700;color:#5eead4;padding:10px 0;border-bottom:1px solid #334155;margin:16px 0 12px}
              .quiz-label.b{color:#818cf8}.quiz-label.c{color:#fbbf24}
            `}</style>
            <CourseQuiz
              quizId="cultural_quiz"
              courseId="cultural"
              title="Kulturelles Deutschland – Quiz"
              sections={CULTURAL_SECTIONS}
              answers={CULTURAL_ANSWERS}
              isActive={true}
            />
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#14b8a6' }} />
            <span className="ml-3" style={{ color: '#94a3b8' }}>Loading content...</span>
          </div>
        ) : (
          MODULES.map(m => (
            m.data && (
              <div key={m.id} className={`section ${activeTab === m.id ? 'active' : ''}`}>
                <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>{m.data.title.de}</h2>
                <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
                  {m.data.title.en} · <span className="ar">{m.data.title.ar}</span>
                </p>
                {m.data.sections.map((section, si) => renderSection(section, si))}
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
}

function CulturalExercisePicker() {
  const [selectedLesson, setSelectedLesson] = useState('C1');
  const keys = ['C1','C2','C3','C4','C5','C6'];
  const labels = ['C1: Begrüßung','C2: Essen','C3: Feiertage','C4: Arbeitskultur','C5: Gesundheit','C6: Wohnen'];
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {keys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-teal-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} exercises={EXERCISES_CULTURAL} />
    </div>
  );
}
